// Pure helpers for drawing 3D bar charts as flat-shaded SVG polygons.
// 3D points are [x, y, z]: x runs left to right, y runs towards the viewer, z is the height.

const toRadians = degrees => (degrees * Math.PI) / 180;

// Parallel (axonometric) projection: the camera is rotated by yaw around the vertical axis and tilted down by elevation.
export const createProjection3d = ({ yawDeg, elevationDeg }) => {
  const yaw = toRadians(yawDeg);
  const elevation = toRadians(elevationDeg);
  const xAxis = [Math.cos(yaw), Math.sin(yaw) * Math.sin(elevation)];
  const yAxis = [-Math.sin(yaw), Math.cos(yaw) * Math.sin(elevation)];
  const heightScale = Math.cos(elevation);

  return ([x, y, z]) => [
    x * xAxis[0] + y * yAxis[0],
    x * xAxis[1] + y * yAxis[1] - z * heightScale,
  ];
};

// Faces of an axis-aligned bar visible to a camera with yaw in (0, 90) degrees. A flat bar only has a top face.
export const getBarFaces = ({
  x0, x1, y0, y1, height,
}) => {
  const top = [[x0, y0, height], [x1, y0, height], [x1, y1, height], [x0, y1, height]];
  if (height <= 0) return { top };

  return {
    top,
    front: [[x0, y1, 0], [x1, y1, 0], [x1, y1, height], [x0, y1, height]],
    side: [[x1, y0, 0], [x1, y1, 0], [x1, y1, height], [x1, y0, height]],
  };
};

export const getBoxCorners = ({ width, depth, height }) => [
  [0, 0, 0], [width, 0, 0], [0, depth, 0], [width, depth, 0],
  [0, 0, height], [width, 0, height], [0, depth, height], [width, depth, height],
];

export const getBounds = points => ({
  minX: Math.min(...points.map(([x]) => x)),
  maxX: Math.max(...points.map(([x]) => x)),
  minY: Math.min(...points.map(([, y]) => y)),
  maxY: Math.max(...points.map(([, y]) => y)),
});

// Returns a mapper that uniformly scales points inside `bounds` to fit `area`, centered.
export const createFitTransform = (bounds, area) => {
  const boundsWidth = bounds.maxX - bounds.minX;
  const boundsHeight = bounds.maxY - bounds.minY;
  const scale = Math.min(area.width / boundsWidth, area.height / boundsHeight);
  const offsetX = area.x + (area.width - boundsWidth * scale) / 2 - bounds.minX * scale;
  const offsetY = area.y + (area.height - boundsHeight * scale) / 2 - bounds.minY * scale;

  return ([x, y]) => [offsetX + x * scale, offsetY + y * scale];
};

export const toSvgPoints = points => points.map(([x, y]) => `${x.toFixed(2)},${y.toFixed(2)}`).join(' ');

const toUnitVector = ([x, y]) => {
  const length = Math.hypot(x, y);
  return [x / length, y / length];
};

// SVG matrix that lays text flat on the ground plane at `anchor` (screen px): text runs along the world x axis and its
// glyphs lean along the world y axis. Both axes are normalized so the font keeps its size and stays readable.
export const getGroundTextTransform = (project, [anchorX, anchorY]) => {
  const [originX, originY] = project([0, 0, 0]);
  const [xAxisX, xAxisY] = project([1, 0, 0]);
  const [yAxisX, yAxisY] = project([0, 1, 0]);
  const [a, b] = toUnitVector([xAxisX - originX, xAxisY - originY]);
  const [c, d] = toUnitVector([yAxisX - originX, yAxisY - originY]);

  return `matrix(${[a, b, c, d, anchorX, anchorY].map(value => value.toFixed(4)).join(' ')})`;
};
